import React from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setActiveTool, setZoomLevel, openView3d, closeView3d } from '../../features/ui/uiSlice';
import { canRedoAny, canUndoAny, redoLast, undoLast } from '../../features/history/unifiedHistory';
import { removeInstances } from '../../features/equipment/equipmentSlice';
import {
  deleteSelectedLayoutElements,
  deleteSelectedTrackNode,
} from '../../utils/deleteCanvasSelection';
import { MIN_ZOOM, MAX_ZOOM } from '../../constants';
import { viewportStore } from '../../store/viewportStore';
import { useViewport } from '../../hooks/useViewport';

const FloatingCanvasMenu: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeTool = useAppSelector((state) => state.ui.activeTool);
  const canUndo = useAppSelector(canUndoAny);
  const canRedo = useAppSelector(canRedoAny);
  const selectedIds = useAppSelector((state) => state.selection.selectedIds);
  const selectedTrackNodeId = useAppSelector((state) => state.layout.selectedTrackNodeId);
  const selectedEquipmentId = useAppSelector((state) => state.equipment.selectedInstanceId);
  const selectedEquipmentIds = useAppSelector((state) => state.equipment.selectedInstanceIds);
  const elements = useAppSelector((state) => state.layout.elements);
  const layoutOrder = useAppSelector((state) => state.layout.elementOrder);
  const floorPolylines = useAppSelector((state) => state.floorplan.polylines);
  const floorBounds = useAppSelector((state) => state.floorplan.bounds);
  const floorplanVisible = useAppSelector((state) => state.floorplan.visible);
  const reduxZoomLevel = useAppSelector((state) => state.ui.zoomLevel);
  const [viewport] = useViewport();
  const view3dOpen = useAppSelector((state) => state.ui.view3dOpen);
  const zoomLevel =
    view3dOpen && Number.isFinite(viewport.zoom) && viewport.zoom > 0
      ? viewport.zoom
      : reduxZoomLevel;
  /** 3D only when a real fab / layout is present — not an empty shell. */
  const canOpen3d =
    layoutOrder.length > 0 ||
    (floorplanVisible && floorPolylines.length > 0 && Boolean(floorBounds));
  const equipmentInstances = useAppSelector((state) => state.equipment.instances);
  // Simulator is read-only, so the editing controls in this menu stay inert.
  const simulationMode = useAppSelector((state) => state.ui.simulationPanelOpen);

  const canDelete =
    !simulationMode &&
    (selectedIds.length > 0 || Boolean(selectedTrackNodeId) || Boolean(selectedEquipmentId));

  const handleDelete = () => {
    if (selectedTrackNodeId) {
      deleteSelectedTrackNode(
        dispatch,
        selectedTrackNodeId,
        elements,
        Object.values(equipmentInstances)
      );
      return;
    }
    if (selectedEquipmentId) {
      dispatch(removeInstances(selectedEquipmentIds));
    }
    if (selectedIds.length > 0) {
      deleteSelectedLayoutElements(dispatch, selectedIds, elements);
    }
  };

  const setZoom = (next: number) => {
    const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, next));
    dispatch(setZoomLevel(clamped));
    viewportStore.setState({ zoom: clamped });
  };

  const zoomLabel =
    zoomLevel < 0.1 ? `${(zoomLevel * 100).toFixed(1)}%` : `${Math.round(zoomLevel * 100)}%`;

  return (
    <div
      className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1 rounded-full bg-white border border-slate-200 shadow-lg px-2 py-1.5"
      role="toolbar"
      aria-label="Canvas tools"
    >
      {/* Select / Pan / Delete */}
      <div className="flex items-center gap-0.5">
        <MenuButton
          active={!simulationMode && activeTool === 'select'}
          disabled={simulationMode}
          label="Select"
          onClick={() => dispatch(setActiveTool('select'))}
        >
          <SelectIcon />
        </MenuButton>
        <MenuButton
          active={simulationMode || activeTool === 'pan'}
          label="Pan"
          onClick={() => dispatch(setActiveTool('pan'))}
        >
          <PanIcon />
        </MenuButton>
        <MenuButton label="Delete" disabled={!canDelete} onClick={handleDelete}>
          <TrashIcon />
        </MenuButton>
      </div>

      <Divider />

      {/* Undo / Redo */}
      <div className="flex items-center gap-0.5">
        <MenuButton label="Undo" disabled={!canUndo} onClick={() => dispatch(undoLast())}>
          <UndoIcon />
        </MenuButton>
        <MenuButton label="Redo" disabled={!canRedo} onClick={() => dispatch(redoLast())}>
          <RedoIcon />
        </MenuButton>
      </div>

      <Divider />

      {/* 2D / 3D */}
      <div className="flex items-center rounded-full bg-slate-100 p-0.5">
        <SegmentButton active={!view3dOpen} onClick={() => dispatch(closeView3d())}>
          2D
        </SegmentButton>
        <SegmentButton
          active={view3dOpen}
          disabled={!canOpen3d}
          title={canOpen3d ? '3D view' : 'Open or import a layout / fab before switching to 3D'}
          onClick={() => {
            if (!canOpen3d) return;
            dispatch(openView3d({ simulate: false }));
          }}
        >
          3D
        </SegmentButton>
      </div>

      <Divider />

      {/* Zoom */}
      <div className="flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5">
        <IconOnlyButton title="Zoom out" onClick={() => setZoom(zoomLevel / 1.25)}>
          <ZoomOutIcon />
        </IconOnlyButton>
        <span className="min-w-12 text-center text-xs font-medium text-slate-600 tabular-nums">
          {zoomLabel}
        </span>
        <IconOnlyButton title="Zoom in" onClick={() => setZoom(zoomLevel * 1.25)}>
          <ZoomInIcon />
        </IconOnlyButton>
      </div>
    </div>
  );
};

const Divider: React.FC = () => (
  <div className="w-px h-6 bg-slate-200 mx-1 shrink-0" aria-hidden="true" />
);

const MenuButton: React.FC<{
  label: string;
  active?: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, active, disabled, title, onClick, children }) => (
  <button
    type="button"
    title={title || label}
    disabled={disabled}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
      disabled
        ? 'text-slate-300 cursor-not-allowed'
        : active
          ? 'bg-slate-100 text-slate-900'
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
    }`}
  >
    <span className="shrink-0">{children}</span>
    {label}
  </button>
);

const SegmentButton: React.FC<{
  active: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, disabled, title, onClick, children }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
      disabled
        ? 'cursor-not-allowed text-slate-300'
        : active
          ? 'bg-white text-slate-900 shadow-sm'
          : 'text-slate-500 hover:text-slate-700'
    }`}
  >
    {children}
  </button>
);

const IconOnlyButton: React.FC<{
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ title, onClick, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    className="flex h-7 w-7 items-center justify-center rounded-full text-slate-500 hover:bg-white hover:text-slate-800 transition-colors"
  >
    {children}
  </button>
);

const iconClass = 'w-3.5 h-3.5';

const SelectIcon: React.FC = () => (
  <svg
    className={iconClass}
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <path d="M2.5 1.5 11.5 7.2 7.2 8.3 5.4 12.5 2.5 1.5Z" strokeLinejoin="round" />
  </svg>
);

const PanIcon: React.FC = () => (
  <svg
    className={iconClass}
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <circle cx="7" cy="7" r="5.25" />
    <path d="M7 3.5v7M3.5 7h7" strokeLinecap="round" />
  </svg>
);

const TrashIcon: React.FC = () => (
  <svg
    className={iconClass}
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <path
      d="M2.5 3.5h9M5 3.5V2.5h4v1M4 3.5l.6 8h4.8l.6-8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UndoIcon: React.FC = () => (
  <svg
    className={iconClass}
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <path d="M4 5.5H9a3 3 0 1 1 0 6H7.5" strokeLinecap="round" />
    <path d="M5.5 3 3.5 5.5 5.5 8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const RedoIcon: React.FC = () => (
  <svg
    className={iconClass}
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <path d="M10 5.5H5a3 3 0 1 0 0 6h1.5" strokeLinecap="round" />
    <path d="M8.5 3 10.5 5.5 8.5 8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ZoomOutIcon: React.FC = () => (
  <svg
    className={iconClass}
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <circle cx="6" cy="6" r="3.5" />
    <path d="M8.5 8.5 12 12M4.5 6h3" strokeLinecap="round" />
  </svg>
);

const ZoomInIcon: React.FC = () => (
  <svg
    className={iconClass}
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <circle cx="6" cy="6" r="3.5" />
    <path d="M8.5 8.5 12 12M4.5 6h3M6 4.5v3" strokeLinecap="round" />
  </svg>
);

export default FloatingCanvasMenu;
